// Utility function to convert Nepali digits to English digits
export const convertNepaliToEnglishDigits = (value) => {
  if (!value || typeof value !== 'string') return value;
  
  const nepaliToEnglish = {
    '०': '0', '१': '1', '२': '2', '३': '3', '४': '4',
    '५': '5', '६': '6', '७': '7', '८': '8', '९': '9'
  };
  
  return value.replace(/[०१२३४५६७८९]/g, (match) => nepaliToEnglish[match]);
};

// Utility function to convert English digits to Nepali digits
export const convertEnglishToNepaliDigits = (value) => {
  if (!value || typeof value !== 'string') return value;
  
  const englishToNepali = {
    '0': '०', '1': '१', '2': '२', '3': '३', '4': '४',
    '5': '५', '6': '६', '7': '७', '8': '८', '9': '९'
  };
  
  return value.replace(/[0-9]/g, (match) => englishToNepali[match]);
};

// Detect if input contains Nepali digits
export const isNepaliInput = (value) => {
  if (!value || typeof value !== 'string') return false;
  return /[०-९]/.test(value);
};

// Detect if input contains English digits
export const isEnglishInput = (value) => {
  if (!value || typeof value !== 'string') return false;
  return /[0-9]/.test(value);
};

// Auto-detect digit format and convert to English for calculations
export const autoDetectAndConvertToEnglish = (value) => {
  if (!value || typeof value !== 'string') return value;
  
  if (isNepaliInput(value)) {
    return convertNepaliToEnglishDigits(value);
  }
  return value;
};

// Auto-detect digit format and convert to Nepali for display
export const autoDetectAndConvertToNepali = (value) => {
  if (!value || typeof value !== 'string') return value;
  
  if (isEnglishInput(value)) {
    return convertEnglishToNepaliDigits(value);
  }
  return value;
};

// Helper function to parse numeric values from input (handles both English and Nepali digits and ignores commas)
export const parseNumericValue = (value) => {
  if (!value && value !== 0) return 0;
  
  // Auto-detect and convert to English, then remove commas
  const englishValue = autoDetectAndConvertToEnglish(value.toString())
    .replace(/,/g, '');  // Remove commas
  
  return parseFloat(englishValue) || 0;
};

// Format number with commas for display (supports both English and Nepali output based on input)
export const formatNumberWithCommas = (value) => {
  if (!value && value !== 0) return '';
  
  const str = value.toString();
  const isNepali = /[०-९]/.test(str);
  const num = parseNumericValue(str);
  
  // Format with commas
  const formatted = num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  
  // Convert to Nepali digits if the input had Nepali digits
  return isNepali ? convertEnglishToNepaliDigits(formatted) : formatted;
};

// Utility function to format number for display (can be used to show in Nepali digits)
export const formatNumberForDisplay = (value, useNepaliDigits = false) => {
  if (!value && value !== 0) return '';
  
  // Convert to string and remove any existing commas
  const numStr = value.toString().replace(/,/g, '');
  
  // Add commas as thousand separators
  const withCommas = numStr.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  
  // Convert to Nepali digits if requested
  return useNepaliDigits ? convertEnglishToNepaliDigits(withCommas) : withCommas;
};

// Smart formatting that maintains user's preferred digit format
export const smartFormatNumber = (value, preferredFormat = 'auto') => {
  if (!value && value !== 0) return '';
  
  // Remove commas and get clean number
  const cleanValue = value.toString().replace(/,/g, '');
  
  // Add commas as thousand separators
  const withCommas = cleanValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  
  // Determine format based on preference or auto-detect
  if (preferredFormat === 'nepali' || (preferredFormat === 'auto' && isNepaliInput(cleanValue))) {
    return convertEnglishToNepaliDigits(withCommas);
  } else if (preferredFormat === 'english' || (preferredFormat === 'auto' && isEnglishInput(cleanValue))) {
    return withCommas;
  }
  
  // Default to English if no preference detected
  return withCommas;
};

// Format number with automatic comma insertion and digit format detection
export const formatWithAutoCommas = (value) => {
  if (!value && value !== 0) return '';
  
  const cleanValue = value.toString().replace(/,/g, '');
  const isNepali = isNepaliInput(cleanValue);
  
  // Add commas
  const withCommas = cleanValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  
  // Maintain the original digit format
  return isNepali ? withCommas : withCommas;
};

// Detect the preferred digit format from a row's values
export const detectRowDigitFormat = (row) => {
  if (!row) return 'english';
  
  // Check all numeric fields in the row
  const numericFields = [
    'unallocatedBudget', 'revisedBudget',
    'q1BudgetDemand', 'q1BudgetExpenditure', 'q1AuthorityReceived', 'q1Expense',
    'q2BudgetDemand', 'q2BudgetExpenditure', 'q2AuthorityReceived', 'q2Expense',
    'q3BudgetDemand', 'q3BudgetExpenditure', 'q3AuthorityReceived', 'q3Expense',
    'q4BudgetDemand', 'q4BudgetExpenditure', 'q4AuthorityReceived', 'q4Expense'
  ];
  
  // First, check if any field has Nepali digits
  for (const field of numericFields) {
    if (row[field] && isNepaliInput(row[field])) {
      return 'nepali';
    }
  }
  
  // Then, check if any field has English digits (indicating active English input)
  for (const field of numericFields) {
    if (row[field] && isEnglishInput(row[field])) {
      return 'english';
    }
  }
  
  // Default to English if no digits detected
  return 'english';
};

// Format calculation result based on row's preferred format
export const formatCalculationResult = (value, row) => {
  if (!value && value !== 0) return '0';
  
  const preferredFormat = detectRowDigitFormat(row);
  const cleanValue = value.toString().replace(/,/g, '');
  const withCommas = cleanValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  
  if (preferredFormat === 'nepali') {
    return convertEnglishToNepaliDigits(withCommas);
  }
  
  return withCommas;
};

// Calculate total with format detection from source data
export const calculateTotalWithFormatDetection = (rows, field) => {
  if (!rows || rows.length === 0) return '0';
  
  // Calculate the total
  const total = rows.reduce((sum, row) => {
    const value = parseNumericValue(row[field] || '0');
    return sum + value;
  }, 0);
  
  // Detect the format from the source data
  let sourceFormat = 'english';
  for (const row of rows) {
    if (row[field]) {
      if (isNepaliInput(row[field])) {
        sourceFormat = 'nepali';
        break;
      } else if (isEnglishInput(row[field])) {
        sourceFormat = 'english';
        break;
      }
    }
  }
  
  // Format the total according to the detected source format
  const cleanValue = total.toString().replace(/,/g, '');
  const withCommas = cleanValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  
  if (sourceFormat === 'nepali') {
    return convertEnglishToNepaliDigits(withCommas);
  }
  
  return withCommas;
};

// Calculate complex total with format detection (for multi-field calculations)
export const calculateComplexTotalWithFormatDetection = (rows, calculationFunction) => {
  if (!rows || rows.length === 0) return '0';
  
  // Calculate the total using the provided function
  const total = rows.reduce((sum, row) => {
    return sum + calculationFunction(row);
  }, 0);
  
  // Detect the format from the source data by checking all numeric fields
  let sourceFormat = 'english';
  const numericFields = [
    'unallocatedBudget', 'revisedBudget',
    'q1BudgetDemand', 'q1BudgetExpenditure', 'q1AuthorityReceived', 'q1Expense',
    'q2BudgetDemand', 'q2BudgetExpenditure', 'q2AuthorityReceived', 'q2Expense',
    'q3BudgetDemand', 'q3BudgetExpenditure', 'q3AuthorityReceived', 'q3Expense',
    'q4BudgetDemand', 'q4BudgetExpenditure', 'q4AuthorityReceived', 'q4Expense'
  ];
  
  for (const row of rows) {
    for (const field of numericFields) {
      if (row[field]) {
        if (isNepaliInput(row[field])) {
          sourceFormat = 'nepali';
          break;
        } else if (isEnglishInput(row[field])) {
          sourceFormat = 'english';
          break;
        }
      }
    }
    if (sourceFormat === 'nepali') break;
  }
  
  // Format the total according to the detected source format
  const cleanValue = total.toString().replace(/,/g, '');
  const withCommas = cleanValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  
  if (sourceFormat === 'nepali') {
    return convertEnglishToNepaliDigits(withCommas);
  }
  
  return withCommas;
};

// Calculate combined total (operational + capital) with format detection
export const calculateCombinedTotalWithFormatDetection = (operationalRows, capitalRows, field) => {
  if ((!operationalRows || operationalRows.length === 0) && (!capitalRows || capitalRows.length === 0)) return '0';
  
  // Calculate the total
  const operationalTotal = operationalRows ? operationalRows.reduce((sum, row) => {
    const value = parseNumericValue(row[field] || '0');
    return sum + value;
  }, 0) : 0;
  
  const capitalTotal = capitalRows ? capitalRows.reduce((sum, row) => {
    const value = parseNumericValue(row[field] || '0');
    return sum + value;
  }, 0) : 0;
  
  const total = operationalTotal + capitalTotal;
  
  // Detect the format from the source data by checking all rows
  let sourceFormat = 'english';
  const numericFields = [
    'unallocatedBudget', 'revisedBudget',
    'q1BudgetDemand', 'q1BudgetExpenditure', 'q1AuthorityReceived', 'q1Expense',
    'q2BudgetDemand', 'q2BudgetExpenditure', 'q2AuthorityReceived', 'q2Expense',
    'q3BudgetDemand', 'q3BudgetExpenditure', 'q3AuthorityReceived', 'q3Expense',
    'q4BudgetDemand', 'q4BudgetExpenditure', 'q4AuthorityReceived', 'q4Expense'
  ];
  
  // Check operational rows
  if (operationalRows) {
    for (const row of operationalRows) {
      for (const fieldName of numericFields) {
        if (row[fieldName]) {
          if (isNepaliInput(row[fieldName])) {
            sourceFormat = 'nepali';
            break;
          } else if (isEnglishInput(row[fieldName])) {
            sourceFormat = 'english';
            break;
          }
        }
      }
      if (sourceFormat === 'nepali') break;
    }
  }
  
  // Check capital rows if format not determined yet
  if (sourceFormat === 'english' && capitalRows) {
    for (const row of capitalRows) {
      for (const fieldName of numericFields) {
        if (row[fieldName]) {
          if (isNepaliInput(row[fieldName])) {
            sourceFormat = 'nepali';
            break;
          } else if (isEnglishInput(row[fieldName])) {
            sourceFormat = 'english';
            break;
          }
        }
      }
      if (sourceFormat === 'nepali') break;
    }
  }
  
  // Format the total according to the detected source format
  const cleanValue = total.toString().replace(/,/g, '');
  const withCommas = cleanValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  
  if (sourceFormat === 'nepali') {
    return convertEnglishToNepaliDigits(withCommas);
  }
  
  return withCommas;
};
